// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState } from 'react';
import clsx from 'clsx';

import { useMergeRefs } from '@amzn/awsui-component-toolkit/internal';
import { getAnalyticsMetadataAttribute } from '@amzn/awsui-component-toolkit/internal/analytics-metadata';

import InternalIcon from '../icon/internal';
import { getBaseProps } from '../internal/base-component';
import TokenList from '../internal/components/token-list';
import { fireNonCancelableEvent } from '../internal/events';
import checkControlled from '../internal/hooks/check-controlled';
import { InternalBaseComponentProps } from '../internal/hooks/use-base-component';
import { useListFocusController } from '../internal/hooks/use-list-focus-controller';
import { SomeRequired } from '../internal/types';
import InternalToken from '../token/internal';
import { TokenGroupProps } from './interfaces';

import tokenListStyles from '../internal/components/token-list/styles.css.js';
import styles from './styles.css.js';

type InternalTokenGroupProps = SomeRequired<TokenGroupProps, 'items' | 'alignment'> &
  InternalBaseComponentProps & {
    isItemReadOnly?: (item: TokenGroupProps.Item) => boolean;
  };

export default function InternalTokenGroup({
  alignment,
  items,
  onDismiss,
  limit,
  i18nStrings,
  disableOuterPadding,
  limitShowFewerAriaLabel,
  limitShowMoreAriaLabel,
  readOnly,
  isItemReadOnly,
  __internalRootRef,
  ...props
}: InternalTokenGroupProps) {
  checkControlled('TokenGroup', 'items', items, 'onDismiss', onDismiss);

  const [nextFocusIndex, setNextFocusIndex] = useState<null | number>(null);
  const tokenListRef = useListFocusController({
    nextFocusIndex,
    onFocusMoved: target => {
      target.focus();
      setNextFocusIndex(null);
    },
    listItemSelector: `.${tokenListStyles['list-item']}`,
    showMoreSelector: `.${tokenListStyles.toggle}`,
  });

  const baseProps = getBaseProps(props);
  const hasItems = items.length > 0;
  const mergedRef = useMergeRefs(__internalRootRef, tokenListRef);
  return (
    <div
      {...baseProps}
      className={clsx(
        baseProps.className,
        styles.root,
        hasItems && styles['has-items'],
        disableOuterPadding && styles['no-padding']
      )}
      ref={mergedRef}
    >
      <TokenList
        alignment={alignment}
        items={items}
        limit={limit}
        renderItem={(item, itemIndex) => (
          <InternalToken
            {...item}
            label={item.label}
            key={itemIndex}
            onDismiss={() => {
              fireNonCancelableEvent(onDismiss, { itemIndex });
              setNextFocusIndex(itemIndex);
            }}
            readOnly={readOnly || isItemReadOnly?.(item)}
            variant={'normal'}
            icon={
              item.iconName || item.iconUrl || item.iconSvg ? (
                <InternalIcon
                  name={item.iconName}
                  svg={item.iconSvg}
                  url={item.iconUrl}
                  ariaLabel={item.iconAlt}
                  size={'normal'}
                />
              ) : undefined
            }
            {...(item.disabled || readOnly
              ? {}
              : getAnalyticsMetadataAttribute({ detail: { position: `${itemIndex + 1}` } }))}
          />
        )}
        i18nStrings={i18nStrings}
        limitShowFewerAriaLabel={limitShowFewerAriaLabel}
        limitShowMoreAriaLabel={limitShowMoreAriaLabel}
        onExpandedClick={isExpanded => {
          if (isExpanded && limit) {
            setNextFocusIndex(limit);
          } else {
            setNextFocusIndex(null);
          }
        }}
      />
    </div>
  );
}



// WEBPACK FOOTER //
// /local/p4clients/pkgbuild-const/src/token-group/internal.tsx